import { useMemo } from 'react';
import { SidebarChartStatic } from './SidebarChartStatic';

interface SidebarChartAnimatedProps {
  width?: number;
  height?: number;
  className?: string;
  duration?: number;
  easing?: string;
  color?: string;
  hoverColor?: string;
  shiftFactor?: number; // Controls how much the blocks shift on hover
  strokeWidth?: number; // Added stroke width parameter
}

export const SidebarChartAnimated = ({
  width = 24,
  height = 24,
  className,
  duration = 0.5,
  easing = 'ease-in-out',
  color = 'currentColor',
  hoverColor = color,
  shiftFactor = 1.8, // Default shift amount - adjust as needed
  strokeWidth = 2 // Default stroke width - can be overridden
}: SidebarChartAnimatedProps) => {
  // Define the original paths and their shifted versions
  const pathData = useMemo(() => {
    // Original paths from SidebarChart.tsx
    const topBlock =
      'M1 3a1 1 0 0 1 1-1h15a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V3Z';
    const middleBlock =
      'M6 10a1 1 0 0 1 1-1h15a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-5Z';
    const bottomBlock =
      'M1 17a1 1 0 0 1 1-1h15a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-5Z';

    return {
      topBlock,
      middleBlock,
      bottomBlock
    };
  }, []);

  // Convert CSS ease-in-out to SVG ease equivalent
  const svgEasing = easing === 'ease-in-out' ? 'ease' : easing;

  // Animation configurations - update to match DashboardIconAnimated
  const animationAttributes = {
    dur: `${duration * 2}s`,
    begin: 'chartParentGroup.mouseover',
    end: 'chartParentGroup.mouseout',
    repeatCount: 'indefinite',
    calcMode: 'spline',
    keySplines: '0.42 0 0.58 1; 0.42 0 0.58 1',
    keyTimes: '0; 0.5; 1',
    restart: 'whenNotActive'
  };

  return (
    <div className='group'>
      {/* Static version - visible by default, hidden on hover */}
      <div className={`group-hover:hidden ${className}`} style={{ width, height }}>
        <SidebarChartStatic
          width={width}
          height={height}
          color={color}
          strokeWidth={strokeWidth}
        />
      </div>

      {/* Animated version - hidden by default, visible on hover */}
      <div className={`hidden group-hover:block ${className}`} style={{ width, height }}>
        <svg
          width={width}
          height={height}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="chartParentGroup">
            {/* Background rect with animation transforms */}
            <rect width="24" height="24" fill="none">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0 0; 0 0.5; 0 0"
                {...animationAttributes}
              />
              <animateTransform
                attributeName="transform"
                type="rotate"
                additive="sum"
                values="0 12 12; 0.5 12 12; 0 12 12"
                {...animationAttributes}
              />
            </rect>

            {/* Top block animation */}
            <path
              d={pathData.topBlock}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="butt"
              strokeLinejoin="miter"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values={`0 0; ${shiftFactor} 0; 0 0`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            {/* Middle block animation */}
            <path
              d={pathData.middleBlock}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="butt"
              strokeLinejoin="miter"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values={`0 0; ${-shiftFactor} 0; 0 0`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            {/* Bottom block animation */}
            <path
              d={pathData.bottomBlock}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="butt"
              strokeLinejoin="miter"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values={`0 0; ${shiftFactor} 0; 0 0`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            {/* Hover capture */}
            <rect
              width="24"
              height="24"
              fill="transparent"
              style={{ cursor: 'pointer' }}
            />
          </g>
        </svg>
      </div>
    </div>
  );
};
